import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import "moment/locale/gu";
import { ArrowLeft, Pencil, Download, ShoppingCart, Building2, Phone, User, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchProcessOrderById,
  getViewProcessOrder,
  setViewProcessOrder,
} from "../../../features/processOrders/processOrdersSlice";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { localizedProcessName, formatSizeLabel, formatSize } from "../../../lib/utils";
import { GET } from "../../../services/axios.service";
import { toastStyles } from "../../../static/styles";
import { t } from "../../../locales";
import { route } from "../../../static/route";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../../../components/ui/table";
import OrderMaterialsTable from "../../../components/processOrders/OrderMaterialsTable";
import { Badge } from "../../../components/ui/badge";

// Aluminium casting lines carry no item at all — they're a raw-material
// intake row instead, so fall back to the raw material (Material + size)
// rather than showing "-" in the Item column.
const itemOrRawMaterial = (d: any): string =>
  d.item?.name || formatSizeLabel(d.size ? { size: d.size.size, material_name: d.size.material?.name } : null);

const ProcessOrderViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const order = useAppSelector(getViewProcessOrder) as any;
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  moment.locale("gu");

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch && !canAccess("process-orders.read")) {
      navigate(route.dashboard);
    }
  }, [loggingUser, selectedBranch]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(fetchProcessOrderById(Number(id))).then((result: any) => {
        if (result.payload?.data?.status === "success") {
          dispatch(setViewProcessOrder(result.payload.data.data));
        }
        setLoading(false);
      });
    }
    return () => {
      dispatch(setViewProcessOrder(null));
    };
  }, [id]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response: any = await GET(`/v1/process-orders/${id}/pdf`, {});
      if (response.data.status === "success") {
        const downloadLink = response.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (!newWindow || newWindow.closed || typeof newWindow.closed == "undefined") {
            throw new Error("Popup blocked");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
    setPdfLoading(false);
  };

  // Deep-linked here (e.g. from the party payment ledger) with no ?process=
  // query param, so — unlike the list/create/edit pages — this page resolves
  // its process definition from the fetched order itself, not the query string.
  const processDefinition = order?.process_definition ?? null;
  const isAluminiumCasting = processDefinition?.handler_key === "casting" && order?.type === "aluminium";
  const showWeightColumn = processDefinition?.rate_basis === "weight" && !isAluminiumCasting;
  const showCuttingSizeColumn = processDefinition?.handler_key === "cutting";
  const totalQty = order?.items?.reduce((s: number, d: any) => s + Number(d.qty || 0), 0) || 0;
  const listRoute = processDefinition ? `${route.processOrders}?process=${processDefinition.slug}` : route.dashboard;
  const editRoute = processDefinition ? `${route.processOrdersEdit.replace(":id", String(order?.id))}?process=${processDefinition.slug}` : route.dashboard;

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">
            {t.processOrders.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          {processDefinition && (
            <>
              <Link to={listRoute} className="text-brand-dark hover:underline">
                {t.processOrders.breadcrumbList}
              </Link>
              <span className="mx-2">&rsaquo;</span>
            </>
          )}
          <span className="text-gray-500">{t.processOrders.viewBreadcrumb}</span>
        </nav>

        <h1 className="text-2xl font-bold text-[#113F67] mb-6">
          {processDefinition ? `${localizedProcessName(processDefinition)} — ${t.processOrders.viewTitle}` : t.processOrders.viewTitle}
        </h1>

        {loading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.processOrders.loading}
              </div>
            </CardContent>
          </Card>
        ) : !order ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {t.processOrders.noRecords}
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-0">
            <CardContent className="p-0">
              {/* Header */}
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#113F67]/10 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-5 w-5 text-[#113F67]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-[#113F67]">{order.doc_no || t.processOrders.orderId}</h2>
                      <p className="text-sm text-gray-500">ID: {order.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                      onClick={() => navigate(listRoute)}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t.processOrders.backToList}
                    </Button>
                    <Button
                      className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                      onClick={handleDownloadPdf}
                      disabled={pdfLoading}
                    >
                      {pdfLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {t.processOrders.exportPdf}
                    </Button>
                    {canAccess("process-orders.update") && !order.has_receipts && (
                      <Button
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-[#113F67] text-[#113F67] hover:bg-[#113F67] hover:text-white text-sm"
                        onClick={() => navigate(editRoute)}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {t.common.edit}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">{t.processOrders.dateLabel}: </span>
                    <span className="font-medium text-gray-700">
                      {moment(order.date).format("DD MMMM, YYYY hh:mm A")}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t.processOrders.totalQty}: </span>
                    <span className="font-medium text-gray-700">{totalQty}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200" />

              {/* Party */}
              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-3 flex items-center gap-2">
                  {t.processOrders.partyLabel}
                  <Badge className="text-[11px]" variant={order.mode === "outsource" ? "default" : "secondary"}>
                    {order.mode === "outsource" ? t.processOrders.modeOutsource : t.processOrders.modeInHouse}
                  </Badge>
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    {order.party?.firm_name ? (
                      <Link
                        to={route.partiesView.replace(":id", String(order.party.id))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#113F67] hover:underline"
                      >
                        {order.party.firm_name}
                      </Link>
                    ) : (
                      <span className="font-semibold text-gray-900">-</span>
                    )}
                  </div>
                  {order.party?.name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <Link
                        to={route.partiesView.replace(":id", String(order.party.id))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#113F67] hover:underline"
                      >
                        {order.party.name}
                      </Link>
                    </div>
                  )}
                  {order.party?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{order.party.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              {/* Items */}
              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-4">{t.processOrders.itemsLabel}</h3>

                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">{t.processOrders.table.srNo}</TableHead>
                        <TableHead>{t.processOrders.itemLabel}</TableHead>
                        <TableHead>{t.processOrders.itemSizeLabel}</TableHead>
                        {showCuttingSizeColumn && <TableHead>{t.processOrders.cuttingSizeLabel}</TableHead>}
                        <TableHead>{t.processOrders.table.subProcess}</TableHead>
                        {showWeightColumn && <TableHead>{t.processOrders.weightLabel}</TableHead>}
                        <TableHead className="text-right">{t.processOrders.qtyLabel}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.items?.map((d: any, i: number) => (
                        <TableRow key={d.id}>
                          <TableCell className="font-medium text-gray-500">{i + 1}</TableCell>
                          <TableCell className="font-medium">{itemOrRawMaterial(d)}</TableCell>
                          <TableCell>{formatSize(d.item_size?.size)}</TableCell>
                          {showCuttingSizeColumn && <TableCell>{formatSize(d.cutting_size)}</TableCell>}
                          <TableCell className="font-medium">{d.sub_process?.name || "-"}</TableCell>
                          {showWeightColumn && <TableCell>{d.weight ?? "-"}</TableCell>}
                          <TableCell className="text-right font-medium">{d.qty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="md:hidden space-y-3">
                  {order.items?.map((d: any, i: number) => (
                    <div key={d.id} className="border rounded-lg p-3 bg-gray-50/50">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                        <span className="font-bold text-[#113F67]">{d.qty}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-gray-500">{t.processOrders.itemLabel}:</span> <span className="font-medium">{itemOrRawMaterial(d)}</span></div>
                        <div className="col-span-2"><span className="text-gray-500">{t.processOrders.itemSizeLabel}:</span> <span className="font-medium">{formatSize(d.item_size?.size)}</span></div>
                        {showCuttingSizeColumn && (
                          <div className="col-span-2"><span className="text-gray-500">{t.processOrders.cuttingSizeLabel}:</span> <span className="font-medium">{formatSize(d.cutting_size)}</span></div>
                        )}
                        {showWeightColumn && (
                          <div className="col-span-2"><span className="text-gray-500">{t.processOrders.weightLabel}:</span> <span className="font-medium">{d.weight ?? "-"}</span></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {processDefinition?.handler_key === "cutting" && order.materials && order.materials.length > 0 && (
                <>
                  <div className="border-t border-gray-200" />
                  <OrderMaterialsTable materials={order.materials} />
                </>
              )}

              <div className="border-t border-gray-200" />

              {/* Account Info */}
              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-4">{t.processOrders.accountInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.processOrders.createdAt}</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{order.created_by_user?.name || "-"}</p>
                        <p className="text-sm text-gray-500">{moment(order.created_at).format("DD MMMM, YYYY hh:mm A")}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.processOrders.updatedAt}</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">{moment(order.updated_at).format("DD MMMM, YYYY hh:mm A")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ProcessOrderViewPage;
